/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║  FONTE DE VERDADE — Alarmes e Configuração DHM4.0                       ║
 * ║                                                                          ║
 * ║  Este arquivo é IDÊNTICO nos três repositórios:                         ║
 * ║    • dhm-hermes-usina  →  src/utils/alarmConfig.ts                      ║
 * ║    • dhm4-api          →  src/utils/alarmConfig.ts                      ║
 * ║    • dhm4-webapp       →  src/utils/alarmConfig.ts                      ║
 * ║                                                                          ║
 * ║  ⚠  Ao alterar qualquer valor, atualizar os TRÊS repositórios.          ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */

// ── Tipos ─────────────────────────────────────────────────────────────────────

export type CorStatus = 'verde' | 'amarelo' | 'vermelho';

export interface EntradaStatus {
    descricao: string;
    cor: CorStatus;
}

// ── Status — Unidade Hidráulica ───────────────────────────────────────────────

export const STATUS_UH: Record<number, EntradaStatus> = {
    1:  { descricao: 'Falta de óleo na unidade hidráulica', cor: 'amarelo' },
    2:  { descricao: 'Temperatura acima da máxima',         cor: 'amarelo' },
    3:  { descricao: 'Temperatura próxima da máxima',       cor: 'vermelho' },
    4:  { descricao: 'Falha no motor 1',                    cor: 'amarelo' },
    5:  { descricao: 'Falha no motor 2',                    cor: 'amarelo' },
    6:  { descricao: 'Falha no motor 3',                    cor: 'amarelo' },
    7:  { descricao: 'Bombas desabilitadas',                cor: 'amarelo' },
    8:  { descricao: 'Falha no filtro de ensujamento',      cor: 'amarelo' },
    9:  { descricao: 'Testes das bombas em andamento',      cor: 'amarelo' },
    20: { descricao: 'Unidade funcionando corretamente',    cor: 'verde'   },
    21: { descricao: 'Sistema operando em local',           cor: 'verde'   },
};

// ── Status — Ternos ───────────────────────────────────────────────────────────

export const STATUS_TERNO: Record<number, EntradaStatus> = {
    1:  { descricao: 'Falha no transmissor de posição lado oposto',             cor: 'amarelo'  },
    2:  { descricao: 'Falha no transmissor de posição lado acionado',           cor: 'amarelo'  },
    3:  { descricao: 'Falha no transmissor de pressão lado oposto',             cor: 'amarelo'  },
    4:  { descricao: 'Falha no transmissor de pressão lado acionado',           cor: 'amarelo'  },
    5:  { descricao: 'Reserva',                                                 cor: 'amarelo'  },
    6:  { descricao: 'Reserva',                                                 cor: 'amarelo'  },
    7:  { descricao: 'Carga inesperada no lado oposto',                         cor: 'amarelo'  },
    8:  { descricao: 'Carga inesperada no lado acionado',                       cor: 'amarelo'  },
    9:  { descricao: 'Vazamento no lado oposto',                                cor: 'amarelo'  },
    10: { descricao: 'Vazamento no lado acionado',                              cor: 'amarelo'  },
    11: { descricao: 'Problemas no acumulador no lado oposto',                  cor: 'amarelo'  },
    12: { descricao: 'Problemas no acumulador no lado acionado',                cor: 'amarelo'  },
    13: { descricao: 'Pressão muito baixa no lado oposto',                      cor: 'amarelo'  },
    14: { descricao: 'Pressão muito baixa no lado acionado',                    cor: 'amarelo'  },
    15: { descricao: 'Valor inválido de pressão máxima no lado oposto',         cor: 'amarelo'  },
    16: { descricao: 'Valor inválido de pressão máxima no lado acionado',       cor: 'amarelo'  },
    17: { descricao: 'Valor inválido de pressão mínima no lado oposto',         cor: 'amarelo'  },
    18: { descricao: 'Valor inválido de pressão mínima no lado acionado',       cor: 'amarelo'  },
    19: { descricao: 'Set point inválido de pressão',                           cor: 'amarelo'  },
    20: { descricao: 'Falha na descarga no lado oposto',                        cor: 'amarelo'  },
    21: { descricao: 'Falha na descarga no lado acionado',                      cor: 'amarelo'  },
    22: { descricao: 'Falha na carga no lado oposto',                           cor: 'amarelo'  },
    23: { descricao: 'Falha na carga no lado acionado',                         cor: 'amarelo'  },
    31: { descricao: 'Rolo superior nivelado',                                  cor: 'verde'    },
    32: { descricao: 'Sistema ajustando pressões',                              cor: 'verde'    },
    33: { descricao: 'Diferença de pressão muito elevada',                      cor: 'vermelho' },
    34: { descricao: 'Diferença de altura muito elevada',                       cor: 'vermelho' },
    35: { descricao: 'Diferenças de pressão e altura muito elevadas',           cor: 'vermelho' },
    36: { descricao: 'Altura e pressão máximas ultrapassadas no lado oposto',   cor: 'vermelho' },
    37: { descricao: 'Altura e pressão máximas ultrapassadas no lado acionado', cor: 'vermelho' },
};

// ── Estados normais (não disparam alarme / mostram verde no UI) ───────────────

/** UH: 9 = autoteste das bombas em andamento, 20 = funcionando corretamente, 21 = operando em local */
export const UH_ESTADOS_BONS: readonly number[] = [9, 20, 21];

/**
 * Terno: apenas 31 (nivelado) e 32 (ajustando pressões) são estados normais.
 * ⚠  33-37 são estados CRÍTICOS (vermelho) — NÃO estão nesta lista e DEVEM gerar alarme.
 */
export const TERNO_ESTADOS_BONS: readonly number[] = [31, 32];

export function isEstadoBomUH(status: number): boolean {
    return (UH_ESTADOS_BONS as number[]).includes(status);
}

export function isEstadoBomTerno(status: number): boolean {
    return (TERNO_ESTADOS_BONS as number[]).includes(status);
}

// ── Thresholds de manutenção ──────────────────────────────────────────────────

export const THRESHOLDS_MANUTENCAO = {
    temperatura: { atencao: 55,  critico: 70,  variacao: 5   },   // °C
    umidade:     { atencao: 70,  critico: 90,  variacao: 10  },   // %
    filtro:      { atencao: 1.5, critico: 2.0, variacao: 0.5 },   // bar
} as const;

export type VarManutencao = keyof typeof THRESHOLDS_MANUTENCAO;

export const VAR_MANUTENCAO_LABEL: Record<VarManutencao, string> = {
    temperatura: 'Temperatura do Óleo',
    umidade:     'Umidade do Óleo',
    filtro:      'Saturação do Filtro',
};

export const VAR_MANUTENCAO_UNIDADE: Record<VarManutencao, string> = {
    temperatura: '°C',
    umidade:     '%',
    filtro:      'bar',
};

// ── Sentinel de sensor não instalado ─────────────────────────────────────────

/** Valor retornado pelo CLP quando o sensor não está presente. Ignorar em todas as lógicas. */
export const SENSOR_FALHA = -222;

/** Valor retornado pelo CLP quando houve erro na leitura do sensor. Ignorar em todas as lógicas. */
export const SENSOR_ERRO = -333;

export function isSensorInvalido(valor: number): boolean {
    return valor === SENSOR_FALHA || valor === SENSOR_ERRO;
}

export function getSensorLabel(valor: number): string | null {
    if (valor === SENSOR_FALHA) return 'Sensor não cadastrado no Obsidian';
    if (valor === SENSOR_ERRO)  return 'Erro na leitura do dado no CLP';
    return null;
}

/**
 * Um terno/UH é considerado ativo para o sistema quando seu status não é 0 (sentinela legado de
 * "não cadastrado") nem SENSOR_FALHA/SENSOR_ERRO (-222/-333). Usar para decidir quais ternos
 * existem de fato numa usina antes de qualquer lógica de alarme/agregação.
 */
export function isTernoAtivo(status: number): boolean {
    return !!status && status !== SENSOR_FALHA && status !== SENSOR_ERRO;
}

// ── Constantes operacionais ───────────────────────────────────────────────────

/** Cooldown entre renotificações do mesmo alarme ativo (30 min) */
export const ALARME_COOLDOWN_MS = 30 * 60 * 1000;

/** Janela para contagem de flapping (60 s) */
export const FLAP_JANELA_MS = 60 * 1000;

/** Trocas de status dentro de FLAP_JANELA_MS para suprimir notificações */
export const FLAP_LIMITE = 3;

/** Estabilidade mínima para reativar notificações após flap (30 min) */
export const FLAP_ESTAVEL_MS = 30 * 60 * 1000;

/** Intervalo mínimo entre emails de manutenção para a mesma variável (24 h) */
export const MANUTENCAO_INTERVALO_MS = 24 * 60 * 60 * 1000;
